/* eslint-disable react/no-unescaped-entities */
import React, { useState, useMemo } from "react";

/**
 * Parse a tarot message that may contain one or more <TAROT_DRAW> blocks.
 * Returns { segments: [{ kind: 'text'|'draw', value: string | DrawObj }] }
 *
 * DrawObj shape:
 *   { spread: string, intent: string, cards: [{position, name, orientation}] }
 *
 * The expected AI-emitted block looks like:
 *   <TAROT_DRAW spread="3-card" intent="past · present · future">
 *     POSITION 1 · The Tower · upright
 *     POSITION 2 · Three of Swords · reversed
 *     POSITION 3 · The Star · upright
 *   </TAROT_DRAW>
 */
const DRAW_RX = /<TAROT_DRAW\s*([^>]*)>([\s\S]*?)<\/TAROT_DRAW>/gi;
const ATTR_RX = /(\w+)\s*=\s*"([^"]*)"/g;
const POS_RX = /POSITION\s+(\d+)\s*[·•:\-–]\s*([^·•|\n]+?)\s*[·•|]\s*(upright|reversed|up|rev)/gi;

export function parseTarotMessage(text) {
  if (!text || !text.includes("<TAROT_DRAW")) {
    return { segments: [{ kind: "text", value: text || "" }], hasDraw: false };
  }
  const segments = [];
  let lastIdx = 0;
  let m;
  DRAW_RX.lastIndex = 0;
  while ((m = DRAW_RX.exec(text)) !== null) {
    if (m.index > lastIdx) {
      segments.push({ kind: "text", value: text.slice(lastIdx, m.index) });
    }
    // Parse attrs
    const attrs = {};
    let a;
    ATTR_RX.lastIndex = 0;
    while ((a = ATTR_RX.exec(m[1] || "")) !== null) {
      attrs[a[1]] = a[2];
    }
    // Parse positions
    const cards = [];
    let p;
    POS_RX.lastIndex = 0;
    while ((p = POS_RX.exec(m[2] || "")) !== null) {
      const orient = p[3].toLowerCase().startsWith("rev") ? "reversed" : "upright";
      cards.push({
        position: parseInt(p[1], 10),
        name: p[2].trim().replace(/^\*+|\*+$/g, ""),
        orientation: orient,
      });
    }
    segments.push({
      kind: "draw",
      value: {
        spread: attrs.spread || "spread",
        intent: attrs.intent || "",
        cards,
      },
    });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    segments.push({ kind: "text", value: text.slice(lastIdx) });
  }
  return { segments, hasDraw: segments.some((s) => s.kind === "draw") };
}

// =============================================================================
// CARD VISUAL  — CSS-only 3D flip
// =============================================================================
const POSITION_LABEL_OVERRIDES = {
  "3-card": ["Past", "Present", "Future"],
  "past-life-3-card": ["Past Life", "Lesson", "Karmic Tie"],
  "5-card": ["Background", "Obstacle", "Advice", "Path", "Outcome"],
  "career-5-card": ["Current", "Obstacle", "Advice", "Best Move", "6mo Outcome"],
  "twinflame-5-card": ["You", "Them", "Bond", "Stage", "Divine Timing"],
  "relationship-7-card": ["You", "Partner", "Bond", "Hidden", "Healing", "3mo Path", "Outcome"],
};

const TAROT_CARD_KEYS = {
  // 22 Major Arcana
  "The Fool": "00-fool", "The Magician": "01-magician", "The High Priestess": "02-priestess",
  "The Empress": "03-empress", "The Emperor": "04-emperor", "The Hierophant": "05-hierophant",
  "The Lovers": "06-lovers", "The Chariot": "07-chariot", "Strength": "08-strength",
  "The Hermit": "09-hermit", "Wheel of Fortune": "10-wheel", "Justice": "11-justice",
  "The Hanged Man": "12-hanged", "Death": "13-death", "Temperance": "14-temperance",
  "The Devil": "15-devil", "The Tower": "16-tower", "The Star": "17-star",
  "The Moon": "18-moon", "The Sun": "19-sun", "Judgement": "20-judgement", "The World": "21-world",
};

const cardSlug = (name) => TAROT_CARD_KEYS[name] || name.toLowerCase()
  .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Simple visual for a card face — keyword + symbolic glyph from a stable map.
// (We can swap to actual Rider-Waite images later; this gives a beautiful
// branded look immediately without bundling 78 PNGs.)
const CARD_GLYPH = {
  "00-fool": "🃟", "01-magician": "🔮", "02-priestess": "🌙", "03-empress": "🌾",
  "04-emperor": "👑", "05-hierophant": "📜", "06-lovers": "💞", "07-chariot": "🏛️",
  "08-strength": "🦁", "09-hermit": "🕯️", "10-wheel": "☸️", "11-justice": "⚖️",
  "12-hanged": "🙃", "13-death": "💀", "14-temperance": "⚗️", "15-devil": "⛓️",
  "16-tower": "🗼", "17-star": "⭐", "18-moon": "🌒", "19-sun": "☀️",
  "20-judgement": "📯", "21-world": "🌍",
};
const SUIT_GLYPH = {
  "wands": "🔥", "cups": "💧", "swords": "⚔️", "pentacles": "💰",
};

function suitOf(name) {
  const lc = name.toLowerCase();
  for (const k of Object.keys(SUIT_GLYPH)) if (lc.includes(k)) return k;
  return null;
}

export function TarotDrawCard({ card, index, label, autoFlipDelay }) {
  const [flipped, setFlipped] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  // Auto-flip in staggered sequence
  React.useEffect(() => {
    const t = setTimeout(() => setFlipped(true), autoFlipDelay);
    return () => clearTimeout(t);
  }, [autoFlipDelay]);

  const slug = cardSlug(card.name);
  const suit = suitOf(card.name);
  const glyph = CARD_GLYPH[slug] || (suit ? SUIT_GLYPH[suit] : "✦");
  const isReversed = card.orientation === "reversed";
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const imgSrc = `${BACKEND}/api/static/tarot/${slug}.jpg`;

  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#7C2D12]/70">
        {label}
      </span>
      <button
        type="button"
        onClick={() => setFlipped((v) => !v)}
        className="tarot-card-flip"
        aria-label={flipped ? `${card.name}, click to hide` : "Tap to reveal card"}
        data-testid={`tarot-card-${index}`}
      >
        <div className={`tarot-card-inner ${flipped ? "is-flipped" : ""}`}>
          {/* BACK */}
          <div className="tarot-card-face tarot-card-back">
            <div className="tarot-card-back-pattern" />
            <div className="tarot-card-back-emblem">✦</div>
          </div>
          {/* FRONT — real Rider-Waite-Smith image, falls back to glyph if missing */}
          <div
            className={`tarot-card-face tarot-card-front ${isReversed ? "is-reversed" : ""} ${!imgFailed ? "is-img-mode" : ""}`}
          >
            {!imgFailed ? (
              <>
                <img
                  src={imgSrc}
                  alt={card.name}
                  loading="lazy"
                  decoding="async"
                  onError={() => setImgFailed(true)}
                  className="tarot-card-img"
                  data-testid={`tarot-card-img-${index}`}
                />
                <div className="tarot-card-img-caption">
                  {card.name}
                  {isReversed && <span className="tarot-card-reversed-badge">R</span>}
                </div>
              </>
            ) : (
              <>
                <div className="tarot-card-glyph">{glyph}</div>
                <div className="tarot-card-name">{card.name}</div>
                {isReversed && (
                  <div className="tarot-card-reversed-pill">reversed</div>
                )}
              </>
            )}
          </div>
        </div>
      </button>
    </div>
  );
}

export function TarotDrawBlock({ draw }) {
  const labels = useMemo(() => {
    const fixed = POSITION_LABEL_OVERRIDES[draw.spread];
    if (fixed && fixed.length >= draw.cards.length) return fixed.slice(0, draw.cards.length);
    if (draw.intent) {
      const parts = draw.intent.split(/[·•|]/).map((s) => s.trim()).filter(Boolean);
      if (parts.length === draw.cards.length) return parts;
    }
    return draw.cards.map((c) => `Position ${c.position}`);
  }, [draw]);

  return (
    <div className="bg-gradient-to-br from-[#F6E4C9] to-[#EFD6A6] border border-amber-300/70
      rounded-2xl rounded-bl-sm px-3 sm:px-4 py-3 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <p className="label-eyebrow !text-[#7C2D12] !text-[10px]">
          ✦ Spread · {draw.spread.replace(/-/g, " ")} · {draw.cards.length} cards
        </p>
        {draw.intent && (
          <span className="text-[10px] text-[#7C2D12]/70 italic truncate max-w-[55%]">
            {draw.intent}
          </span>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-3 sm:gap-4 justify-center">
        {draw.cards.map((c, i) => (
          <TarotDrawCard key={`${c.name}-${i}`} card={c}
            index={i + 1} label={labels[i]} autoFlipDelay={400 + i * 350} />
        ))}
      </div>
      <p className="mt-3 text-[10px] text-[#7C2D12]/60 text-center font-mono">
        Tap any card to flip it back · reading below ↓
      </p>
    </div>
  );
}

/**
 * Drop-in renderer for an AI tarot message. Parses out any <TAROT_DRAW>
 * blocks and renders them as flipping-card visuals; the rest of the text
 * is shown in the standard message bubble.
 */
export default function TarotMessage({ text }) {
  const { segments, hasDraw } = useMemo(() => parseTarotMessage(text), [text]);
  if (!hasDraw) return null;
  return (
    <div className="flex flex-col gap-2 items-start max-w-[92%] sm:max-w-[88%]">
      {segments.map((seg, i) => {
        if (seg.kind === "draw") {
          return <TarotDrawBlock key={i} draw={seg.value} />;
        }
        const t = (seg.value || "").trim();
        if (!t) return null;
        return (
          <div key={i}
            className="bg-[#FAF4E8] border border-soft text-[#1A1410] rounded-2xl rounded-bl-sm
              px-3.5 sm:px-4 py-2 sm:py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words">
            {t}
          </div>
        );
      })}
    </div>
  );
}
